import React from 'react';
import { CheckCircle2, RotateCcw, Home, ShieldCheck, Clock, Calendar, UserCheck } from 'lucide-react';
import { ROLES, VALUE_LABELS, CONDUCTS } from '../../data/observationEngine';

const conductaLabel = (conducta) => {
  if (!conducta) return '—';
  if (conducta.label && conducta.label !== conducta.code) return conducta.label;
  const dom = (conducta.code || '').split('-')[0];
  const found = (CONDUCTS[dom] || []).find((c) => c.code === conducta.code);
  return found?.label || conducta.code || '—';
};

/**
 * Confirmación (§32).
 * NO mostrar puntaje ni etiqueta de nivel al observador.
 * SÍ mostrar fecha, hora y responsable + resumen + acciones.
 */
const ScreenConfirmacion = ({ dominio, draft, session, mode, onNueva, onHome }) => {
  const now = new Date();
  const fecha = now.toLocaleDateString('es-CL', { day: '2-digit', month: 'long', year: 'numeric' });
  const hora = now.toLocaleTimeString('es-CL', { hour: '2-digit', minute: '2-digit', hour12: false });
  const rolLabel = ROLES.find((r) => r.v === session?.rol)?.label || 'Equipo de convivencia';
  const responsable = session?.nombre || rolLabel;

  return (
    <div className="step-enter flex flex-col h-full">
      <div className="px-7 pt-10 pb-6 text-center">
        <div className="relative inline-flex">
          <span className="absolute inset-0 rounded-full animate-ping" style={{ background: 'rgba(30,159,115,0.25)' }} />
          <span className="success-pop relative w-20 h-20 rounded-full flex items-center justify-center" style={{ background: '#DFF6EC', color: '#1E9F73' }}>
            <CheckCircle2 className="w-12 h-12" />
          </span>
        </div>

        <h2 className="mt-7 text-[26px] font-extrabold text-brand-deep leading-tight" style={{ fontFamily: 'Plus Jakarta Sans, sans-serif' }}>
          Observación registrada correctamente.
        </h2>
        <p className="mt-3 text-sm text-brand-muted leading-relaxed">
          La información quedará disponible para el equipo autorizado según los protocolos del establecimiento.
        </p>
      </div>

      <div className="px-5 space-y-3 flex-1 overflow-y-auto pb-2">
        {/* Metadatos auditables */}
        <div className="rounded-2xl bg-white border border-[#DDE6F7] p-4" data-testid="confirm-metadatos">
          <p data-testid="confirm-step-label" className="text-[13px] font-bold text-brand-violet">
            Paso 5 de 5 – Confirmación
          </p>
          <ul className="mt-3 space-y-3 text-sm">
            <Row icon={Calendar} label="Fecha" value={fecha} testid="confirm-fecha" />
            <Row icon={Clock} label="Hora" value={hora} testid="confirm-hora" />
            <Row icon={UserCheck} label="Responsable" value={responsable} testid="confirm-responsable" sub={rolLabel} />
          </ul>
        </div>

        {/* Resumen de la observación */}
        <div className="rounded-2xl bg-white border border-[#DDE6F7] p-4" data-testid="confirm-resumen">
          <p className="text-sm font-bold text-brand-deep">Resumen</p>
          <ul className="mt-3 space-y-2.5 text-sm">
            <PillRow label="Dominio" value={dominio.name} pill={{ bg: dominio.bg, fg: dominio.color }} />
            <PillRow label="Conducta" value={conductaLabel(draft.conducta)} />
            <PillRow label="Curso" value={draft.curso || '—'} />
            <PillRow label="Contexto" value={VALUE_LABELS[draft.contexto] || '—'} />
            <PillRow label="Intensidad" value={VALUE_LABELS[draft.intensidad] || '—'} />
            {draft.frecuencia && <PillRow label="Frecuencia" value={VALUE_LABELS[draft.frecuencia] || '—'} />}
            {mode === 'rapido' && (
              <li className="pt-1 text-[11.5px] text-brand-muted">
                Modo rápido: duración, participantes y fuente quedaron con valores conservadores por defecto.
              </li>
            )}
          </ul>
        </div>

        <div className="rounded-2xl bg-[#EEF4FF] border border-[#DDE6F7] p-4 flex items-start gap-3" data-testid="confirm-trazabilidad">
          <span className="w-9 h-9 rounded-xl bg-white text-brand-violet flex items-center justify-center shrink-0 border border-[#DDE6F7]">
            <ShieldCheck className="w-5 h-5" />
          </span>
          <div className="text-xs text-brand-deep leading-relaxed">
            <strong>Trazabilidad activada.</strong>{' '}
            Quedó registrada con fecha, hora, responsable y fuente. El equipo de convivencia
            podrá revisar y dar seguimiento.
          </div>
        </div>
      </div>

      <div className="px-5 pb-6 pt-3 space-y-2 border-t border-[#F1F4FA] bg-white">
        <button data-testid="confirm-nueva" onClick={onNueva} className="m-btn-primary">
          <RotateCcw className="w-4 h-4" />
          Registrar otra observación
        </button>
        <button data-testid="confirm-home" onClick={onHome} className="m-btn-ghost">
          <Home className="w-4 h-4" />
          Volver al inicio
        </button>
      </div>
    </div>
  );
};

const Row = ({ icon: Icon, label, value, sub, testid }) => (
  <li className="flex items-start gap-3" data-testid={testid}>
    <span className="w-9 h-9 rounded-xl bg-[#EEF4FF] text-brand-violet flex items-center justify-center shrink-0">
      <Icon className="w-4 h-4" />
    </span>
    <div className="min-w-0 flex-1">
      <p className="text-[12px] font-semibold text-brand-muted">{label}</p>
      <p className="text-[14px] font-bold text-brand-deep truncate">{value}</p>
      {sub && <p className="text-[11px] text-brand-muted truncate">{sub}</p>}
    </div>
  </li>
);

const PillRow = ({ label, value, pill }) => (
  <li className="flex items-center justify-between gap-3">
    <span className="text-brand-muted text-[13px] font-semibold">{label}</span>
    {pill ? (
      <span className="text-xs font-extrabold px-2.5 py-1 rounded-md" style={{ background: pill.bg, color: pill.fg }}>{value}</span>
    ) : (
      <span className="text-brand-deep font-bold text-right max-w-[200px] truncate">{value}</span>
    )}
  </li>
);

export default ScreenConfirmacion;
